import { useEffect, useRef, useState } from 'react';
import {
  productUrlForDisplay,
  withShopsMtOutboundUrl,
} from '../outboundUrl';
import { trackClick } from '../analytics';
import {
  resolveGaSourcePage,
  trackOpenSignupModal,
  trackOutboundClick,
  trackReportProductOpen,
} from '../ga';
import {
  dismissProductReturnCta,
  markProductOutboundVisit,
  shouldShowReturnCtaForProduct,
  subscribeReturnCta,
} from '../productCardReturnCta';
import {
  observeProductImpression,
  unobserveProductImpression,
} from '../productImpressionObserver';
import { addRecentlyViewedProduct, isRecentlyViewedProduct, removeRecentlyViewedProduct, subscribeRecentlyViewedProducts } from '../recentlyViewedProducts';
import { getPremiumShopForProduct } from '../premiumShops';
import { FavoriteSignupModal } from './FavoriteSignupModal';
import { ProductCardReturnCta } from './ProductCardReturnCta';
import { ReportModal } from './ReportModal';

const eurPriceFormatter = new Intl.NumberFormat('mt-MT', {
  style: 'currency',
  currency: 'EUR',
});

const eurWholeFormatter = new Intl.NumberFormat('mt-MT', {
  maximumFractionDigits: 0,
});

function splitPriceAmount(amount) {
  const roundedCents = Math.round(Number(amount) * 100);
  const whole = Math.trunc(roundedCents / 100);
  const cents = String(Math.abs(roundedCents % 100)).padStart(2, '0');

  return {
    whole: eurWholeFormatter.format(whole),
    cents,
  };
}

function isZeroPrice(amount) {
  return amount != null && Number(amount) === 0;
}

function formatPriceAriaLabel(amount) {
  if (amount == null || Number.isNaN(Number(amount))) {
    return 'Price unavailable';
  }
  return eurPriceFormatter.format(Number(amount));
}

/**
 * When priceNow is 0, show compare-at as the price or "?" if none.
 * @returns {{ displayNow: number|null, displayWas: number|null, priceUnknown: boolean }}
 */
function resolveProductPrices(priceNow, priceWas) {
  if (isZeroPrice(priceNow)) {
    if (priceWas != null && !isZeroPrice(priceWas)) {
      return { displayNow: priceWas, displayWas: null, priceUnknown: false };
    }
    return { displayNow: null, displayWas: null, priceUnknown: true };
  }

  if (priceNow == null) {
    if (priceWas != null && !isZeroPrice(priceWas)) {
      return { displayNow: priceWas, displayWas: null, priceUnknown: false };
    }
    return { displayNow: null, displayWas: null, priceUnknown: false };
  }

  const hasDiscount =
    priceWas != null &&
    !isZeroPrice(priceWas) &&
    Number(priceNow) < Number(priceWas);

  return {
    displayNow: priceNow,
    displayWas: hasDiscount ? priceWas : null,
    priceUnknown: false,
  };
}

function PriceAmount({ amount }) {
  if (amount == null || Number.isNaN(Number(amount))) {
    return '—';
  }

  const { whole, cents } = splitPriceAmount(amount);

  return (
    <span className="product-card__price-amount">
      €{whole}
      <span className="product-card__price-cents">{cents}</span>
    </span>
  );
}

/** @returns {number|null} Rounded percent off when there is a real discount, else null. */
function discountPercentOff(priceWas, priceNow) {
  if (priceWas == null || priceNow == null) {
    return null;
  }
  const was = Number(priceWas);
  const now = Number(priceNow);
  if (!Number.isFinite(was) || !Number.isFinite(now) || was <= 0 || now >= was) {
    return null;
  }
  const pct = Math.round(((was - now) / was) * 100);
  return pct > 0 ? pct : null;
}

/** @returns {number|null} Euro amount saved when there is a real discount, else null. */
function discountEuroOff(priceWas, priceNow) {
  if (priceWas == null || priceNow == null) {
    return null;
  }
  const was = Number(priceWas);
  const now = Number(priceNow);
  if (!Number.isFinite(was) || !Number.isFinite(now) || now >= was) {
    return null;
  }
  const off = was - now;
  return off > 0 ? off : null;
}

function formatDiscountEuroBadge(amount) {
  const roundedCents = Math.round(Number(amount) * 100);
  const whole = Math.trunc(roundedCents / 100);
  const cents = Math.abs(roundedCents % 100);

  if (cents === 0) {
    return eurWholeFormatter.format(whole);
  }

  return `${eurWholeFormatter.format(whole)},${String(cents).padStart(2, '0')}`;
}

function DotsIconSmall() {
  return (
    <svg width="14" height="14" viewBox="0 0 20 20" aria-hidden="true">
      <circle cx="10" cy="4.5" r="1.35" fill="currentColor" />
      <circle cx="10" cy="10" r="1.35" fill="currentColor" />
      <circle cx="10" cy="15.5" r="1.35" fill="currentColor" />
    </svg>
  );
}

function ShippingIcon() {
  return (
    <svg
      className="product-card__shipping-icon"
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M3 7.5h11.5v9H8.2M14.5 10.5H19l2 3.2v3.3h-2.2"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="7" cy="17.5" r="1.75" stroke="currentColor" strokeWidth="1.75" />
      <circle cx="17.5" cy="17.5" r="1.75" stroke="currentColor" strokeWidth="1.75" />
    </svg>
  );
}

function productAlertMenuLabel(inStock, priceUnknown) {
  if (inStock === false) {
    return 'Add in-stock alert';
  }
  if (priceUnknown) {
    return 'Add price alert';
  }
  return 'Watch this product';
}

function CardDotsMenu({
  menuId,
  alertDisabled,
  alertLabel,
  onSaveAlert,
  onReportIssue,
  showRemoveRecentlyViewed = false,
  onRemoveRecentlyViewed,
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  function closeMenu() {
    setOpen(false);
  }

  useEffect(() => {
    function handlePointerDown(e) {
      if (!rootRef.current?.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
    };
  }, []);

  useEffect(() => {
    function handleKey(e) {
      if (e.key === 'Escape') {
        setOpen(false);
      }
    }
    if (open) {
      document.addEventListener('keydown', handleKey);
      return () => document.removeEventListener('keydown', handleKey);
    }
    return undefined;
  }, [open]);

  return (
    <div className="product-card__dots-menu" ref={rootRef}>
      <button
        type="button"
        className="product-card__dots-btn"
        aria-expanded={open}
        aria-haspopup="true"
        aria-controls={menuId}
        aria-label="Product options"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setOpen((v) => !v);
        }}
      >
        <DotsIconSmall />
      </button>
      {open && (
        <ul
          id={menuId}
          className="product-card__dots-dropdown"
          role="menu"
          aria-label="Product options"
        >
          <li role="none">
            <button
              type="button"
              role="menuitem"
              className={
                alertDisabled
                  ? 'product-card__dots-item product-card__dots-item--disabled'
                  : 'product-card__dots-item'
              }
              disabled={alertDisabled}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (!alertDisabled) {
                  closeMenu();
                  onSaveAlert();
                }
              }}
            >
              {alertLabel}
            </button>
          </li>
          {showRemoveRecentlyViewed && (
            <li role="none">
              <button
                type="button"
                role="menuitem"
                className="product-card__dots-item"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  closeMenu();
                  onRemoveRecentlyViewed?.();
                }}
              >
                Remove from recently viewed
              </button>
            </li>
          )}
          <li role="none">
            <button
              type="button"
              role="menuitem"
              className="product-card__dots-item product-card__dots-item--report"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                closeMenu();
                onReportIssue();
              }}
            >
              Report issue
            </button>
          </li>
        </ul>
      )}
    </div>
  );
}

function availabilityLabel(inStock) {
  if (inStock === true) {
    return 'In stock';
  }
  if (inStock === false) {
    return 'Out of stock';
  }
  return 'Check availability';
}

function PricesBlock({
  displayNow,
  displayWas,
  priceUnknown,
}) {
  const hasDiscount = displayWas != null;

  return (
    <div className="product-card__prices" aria-label="Price">
      {priceUnknown && (
        <span className="product-card__price-single" aria-label="Price unknown">
          ?
        </span>
      )}
      {!priceUnknown && displayNow != null && (
        <span
          className={
            hasDiscount ? 'product-card__price-now' : 'product-card__price-single'
          }
          aria-label={formatPriceAriaLabel(displayNow)}
        >
          <PriceAmount amount={displayNow} />
        </span>
      )}
      {hasDiscount && (
        <span
          className="product-card__price-was"
          aria-label={formatPriceAriaLabel(displayWas)}
        >
          <PriceAmount amount={displayWas} />
        </span>
      )}
    </div>
  );
}

/**
 * @param {{
 *   product: object,
 *   layout?: 'grid' | 'list',
 *   outboundCampaign?: string,
 *   outboundContent?: string,
 *   analyticsContext?: {
 *     query?: string|null,
 *     shopCategorySlug?: string|null,
 *     source?: string,
 *     page?: number|null,
 *     position?: number|null,
 *     searchFallbackStep?: number|null,
 *   },
 *   enableReturnCta?: boolean,
 *   discountBadgePlacement?: 'image' | 'card-top',
 *   editionBadge?: string|null,
 *   premiumShop?: {
 *     rootDomain: string,
 *     name: string,
 *     faviconUrl?: string,
 *     shippingMessage?: string,
 *   }|null,
 *   annotatePremiumFeatures?: boolean,
 * }} props
 */
export function ProductCard({
  product,
  layout = 'grid',
  outboundCampaign,
  outboundContent = 'product_card',
  analyticsContext,
  enableReturnCta = false,
  discountBadgePlacement = 'image',
  editionBadge = null,
  premiumShop: premiumShopOverride = null,
  annotatePremiumFeatures = false,
}) {
  const [favoriteOpen, setFavoriteOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [showReturnCta, setShowReturnCta] = useState(false);
  const [isRecentlyViewed, setIsRecentlyViewed] = useState(() =>
    isRecentlyViewedProduct(product),
  );
  const rootRef = useRef(null);
  const analyticsContextRef = useRef(analyticsContext);
  analyticsContextRef.current = analyticsContext;

  const {
    id,
    title,
    url,
    image,
    currency,
    priceWas,
    priceNow,
    merchant,
    inStock,
    description,
    additionalImageCount = 0,
  } = product;

  const showImage =
    typeof image === 'string' && image.trim().length > 0;

  const extraImages =
    typeof additionalImageCount === 'number' &&
    additionalImageCount > 0
      ? additionalImageCount
      : 0;

  const totalPhotos = extraImages > 0 ? extraImages + 1 : 0;

  const mediaAriaLabel =
    totalPhotos > 1
      ? `View ${title} — ${totalPhotos} photos`
      : `View ${title}`;

  const { displayNow, displayWas, priceUnknown } = resolveProductPrices(
    priceNow,
    priceWas,
  );

  const hasDiscount =
    !isZeroPrice(priceNow) &&
    priceNow != null &&
    priceWas != null &&
    Number(priceNow) < Number(priceWas);

  const discountPct = hasDiscount
    ? discountPercentOff(priceWas, priceNow)
    : null;

  const discountEuro = hasDiscount
    ? discountEuroOff(priceWas, priceNow)
    : null;

  const showImageDiscountBadge =
    discountPct != null && discountBadgePlacement === 'image';

  const showCardTopDiscountBanner =
    discountPct != null &&
    discountEuro != null &&
    discountBadgePlacement === 'card-top';

  const availabilityClass =
    inStock === true
      ? 'product-card__stock-dot--in'
      : inStock === false
        ? 'product-card__stock-dot--out'
        : 'product-card__stock-dot--unknown';

  const descText =
    typeof description === 'string' && description.trim().length > 0
      ? description.trim()
      : null;

  const availText = availabilityLabel(inStock);

  function openAlertSignup() {
    trackOpenSignupModal('product_price_alert', 'product');
    setFavoriteOpen(true);
  }

  const productUrl = typeof url === 'string' ? url.trim() : '';
  const productUrlOut = withShopsMtOutboundUrl(productUrl, {
    content: outboundContent,
    campaign: outboundCampaign,
  });
  const hasProductLink = productUrlOut !== '';
  const merchantText =
    typeof merchant === 'string' ? merchant.trim() : '';

  const premiumShop =
    premiumShopOverride ?? getPremiumShopForProduct({ url: productUrl, merchant });
  const isPremium = premiumShop != null;
  const premiumShopName =
    typeof premiumShop?.name === 'string' && premiumShop.name.trim() !== ''
      ? premiumShop.name.trim()
      : merchantText;
  const premiumFaviconUrl =
    typeof premiumShop?.faviconUrl === 'string' &&
    premiumShop.faviconUrl.trim() !== ''
      ? premiumShop.faviconUrl.trim()
      : '';
  const premiumShippingMessage =
    typeof premiumShop?.shippingMessage === 'string' &&
    premiumShop.shippingMessage.trim() !== ''
      ? premiumShop.shippingMessage.trim()
      : '';

  const productId =
    typeof id === 'string'
      ? id.trim()
      : typeof id === 'number' && Number.isFinite(id)
        ? String(id)
        : '';

  useEffect(() => {
    function syncRecentlyViewed() {
      setIsRecentlyViewed(isRecentlyViewedProduct(product));
    }

    syncRecentlyViewed();
    return subscribeRecentlyViewedProducts(syncRecentlyViewed);
  }, [product, productUrl]);

  const trackViewportImpressions = analyticsContext != null;

  useEffect(() => {
    const el = rootRef.current;
    if (!el || !trackViewportImpressions || productId === '') {
      return undefined;
    }

    observeProductImpression(el, () => {
      const ctx = analyticsContextRef.current;
      if (!ctx) {
        return null;
      }
      return {
        listingProductId: productId,
        query: ctx.query ?? null,
        shopCategorySlug: ctx.shopCategorySlug ?? null,
        source: ctx.source ?? 'browse',
      };
    });

    return () => {
      unobserveProductImpression(el);
    };
  }, [trackViewportImpressions, productId]);

  const urlDisplay =
    productUrlOut !== ''
      ? productUrlForDisplay(productUrlOut)
      : productUrl !== ''
        ? productUrlForDisplay(productUrl)
        : '';

  function trackMysqlOutboundClick() {
    if (!analyticsContext || productId === '') {
      return;
    }
    trackClick({
      listingProductId: productId,
      query: analyticsContext.query ?? null,
      shopCategorySlug: analyticsContext.shopCategorySlug ?? null,
      source: analyticsContext.source ?? outboundCampaign,
      page: analyticsContext.page ?? null,
      position: analyticsContext.position ?? null,
      searchFallbackStep: analyticsContext.searchFallbackStep ?? null,
    });
  }

  function trackGaOutbound(clickTarget) {
    const sourceContext = analyticsContext?.source ?? outboundCampaign ?? 'browse';
    trackOutboundClick({
      clickTarget,
      sourcePage: resolveGaSourcePage(outboundCampaign, analyticsContext?.source),
      sourceContext,
      position: analyticsContext?.position ?? null,
    });
  }

  function handleProductOutboundClick() {
    if (productUrl === '') {
      return;
    }
    if (enableReturnCta) {
      markProductOutboundVisit(productUrl);
      setShowReturnCta(false);
    }
    // Defer so recently-viewed re-render does not cancel the same click navigation.
    queueMicrotask(() => {
      addRecentlyViewedProduct(product);
    });
  }

  function dismissReturnCta() {
    dismissProductReturnCta(productUrl);
    setShowReturnCta(false);
  }

  useEffect(() => {
    if (!enableReturnCta || !hasProductLink || productUrl === '') {
      setShowReturnCta(false);
      return undefined;
    }

    if (shouldShowReturnCtaForProduct(productUrl)) {
      setShowReturnCta(true);
    }

    return subscribeReturnCta((activeProductUrl) => {
      setShowReturnCta(activeProductUrl === productUrl);
    });
  }, [enableReturnCta, hasProductLink, productUrl]);

  const visitStoreLink =
    hasProductLink && !isPremium ? (
      <a
        className="product-card__visit-store"
        href={productUrlOut}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={
          merchantText !== ''
            ? `Visit ${merchantText} for ${title}`
            : `Visit store for ${title}`
        }
        onClick={(e) => {
          e.stopPropagation();
          trackMysqlOutboundClick();
          trackGaOutbound('visit_store_button');
          handleProductOutboundClick();
        }}
      >
        Visit store
      </a>
    ) : null;

  const premiumVisitLink =
    hasProductLink && isPremium ? (
      <a
        className="product-card__premium-cta"
        href={productUrlOut}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={
          premiumShopName !== ''
            ? `Visit ${premiumShopName} for ${title}`
            : `Visit store for ${title}`
        }
        onClick={(e) => {
          e.stopPropagation();
          trackMysqlOutboundClick();
          trackGaOutbound('visit_store_button');
          handleProductOutboundClick();
        }}
        {...(annotatePremiumFeatures
          ? { 'data-premium-feature': 'cta' }
          : {})}
      >
        {premiumFaviconUrl !== '' && (
          <span className="product-card__premium-favicon" aria-hidden="true">
            <img
              className="product-card__premium-favicon-img"
              src={premiumFaviconUrl}
              alt=""
              loading="lazy"
              decoding="async"
            />
          </span>
        )}
        <span className="product-card__premium-cta-label">
          Visit{' '}
          <span className="product-card__premium-cta-shop">
            {premiumShopName !== '' ? premiumShopName : 'store'}
          </span>
        </span>
      </a>
    ) : null;

  const shopNameForFooter =
    isPremium && premiumShopName !== ''
      ? premiumShopName
      : merchantText;

  const sourceFooter =
    shopNameForFooter !== '' || urlDisplay !== '' || (isPremium && premiumFaviconUrl !== '') ? (
      <div className="product-card__source-meta">
        {isPremium && premiumFaviconUrl !== '' && (
          <span
            className="product-card__premium-favicon product-card__premium-favicon--mini-only"
            aria-hidden="true"
          >
            <img
              className="product-card__premium-favicon-img"
              src={premiumFaviconUrl}
              alt=""
              loading="lazy"
              decoding="async"
            />
          </span>
        )}
        {shopNameForFooter !== '' && (
          <p className="product-card__source-label">{shopNameForFooter}</p>
        )}
        {urlDisplay !== '' && (
          <p className="product-card__url-row">
            <span
              className="product-card__url"
              title={productUrlOut || productUrl}
            >
              {urlDisplay}
            </span>
          </p>
        )}
      </div>
    ) : null;

  const storeBlock =
    premiumVisitLink || visitStoreLink || sourceFooter ? (
      <div className="product-card__store-block">
        {premiumVisitLink}
        {visitStoreLink}
        {sourceFooter}
      </div>
    ) : null;

  const articleClass = [
    'product-card',
    isPremium ? 'product-card--premium' : 'product-card--standard',
    annotatePremiumFeatures ? 'product-card--annotate' : null,
    layout === 'list' ? 'product-card--layout-list' : null,
    !showImage ? 'product-card--no-image' : null,
    showReturnCta ? 'product-card--return-cta-open' : null,
    showCardTopDiscountBanner ? 'product-card--discount-banner' : null,
  ]
    .filter(Boolean)
    .join(' ');

  const pricesEl = (
    <PricesBlock
      displayNow={displayNow}
      displayWas={displayWas}
      priceUnknown={priceUnknown}
    />
  );

  const priceSlot = (
    <div className="product-card__price-slot">{pricesEl}</div>
  );

  const availabilityBlock = (
    <p
      className={
        layout === 'list'
          ? 'product-card__availability product-card__availability--list'
          : 'product-card__availability'
      }
      role="status"
      aria-label={
        isPremium
          ? `Availability: ${availText}, checked today`
          : `Availability: ${availText}`
      }
    >
      <span
        className={`product-card__stock-dot ${availabilityClass}`}
        aria-hidden="true"
      />
      <span className="product-card__availability-text">{availText}</span>
      {isPremium && (
        <span
          className="product-card__checked-today"
          aria-hidden="true"
          {...(annotatePremiumFeatures
            ? { 'data-premium-feature': 'freshness' }
            : {})}
        >
          {' '}
          ✓ today
        </span>
      )}
      {inStock === false && (
        <button
          type="button"
          className="product-card__stock-notify-btn"
          disabled={!hasProductLink}
          aria-label="Get stock status alerts for this product"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (hasProductLink) {
              openAlertSignup();
            }
          }}
        >
          Notify me
        </button>
      )}
    </p>
  );

  const titleRow = <p className="product-card__title">{title}</p>;

  const priceBlock = (
    <div className="product-card__price-block">
      <div className="product-card__price-anchor">
        <div className="product-card__price-row">{priceSlot}</div>
        {enableReturnCta && showReturnCta && hasProductLink && (
          <ProductCardReturnCta
            productUrl={productUrl}
            inStock={inStock}
            priceUnknown={priceUnknown}
            displayNow={displayNow}
            onDismiss={dismissReturnCta}
          />
        )}
      </div>
      {availabilityBlock}
    </div>
  );

  const footerBlock = storeBlock ? (
    <div className="product-card__footer">{storeBlock}</div>
  ) : null;

  const descriptionBlock =
    descText != null ? (
      <div
        className="product-card__desc-slot"
        {...(annotatePremiumFeatures && isPremium
          ? { 'data-premium-feature': 'description' }
          : {})}
      >
        <p
          className={
            isPremium
              ? 'product-card__desc product-card__desc--premium'
              : 'product-card__desc product-card__desc--standard'
          }
          title={descText}
        >
          {descText}
        </p>
      </div>
    ) : null;

  const bodyContent = (
    <>
      <div className="product-card__content">
        {titleRow}
        {priceBlock}
      </div>
      {descriptionBlock}
      {footerBlock}
    </>
  );

  return (
    <article
      ref={rootRef}
      className={articleClass}
      {...(productId !== '' ? { 'data-product-id': productId } : {})}
    >
      {showCardTopDiscountBanner && (
        <div
          className="product-card__discount-banner"
          aria-label={`${discountPct}% off, save ${formatDiscountEuroBadge(discountEuro)} euros`}
        >
          <span className="product-card__discount-banner-pct">
            −{discountPct}%
          </span>
          <span className="product-card__discount-banner-euro">
            −{formatDiscountEuroBadge(discountEuro)}€
          </span>
        </div>
      )}
      <div
        className={
          showImage
            ? 'product-card__media'
            : 'product-card__media product-card__media--placeholder'
        }
        aria-hidden={hasProductLink ? 'true' : undefined}
      >
        <div
          className={
            showImage
              ? 'product-card__media-inner'
              : 'product-card__media-inner product-card__media-inner--placeholder'
          }
        >
          {extraImages > 0 && (
            <>
              <span className="sr-only">
                {totalPhotos} product photos — this card shows the first.
              </span>
              <span className="product-card__gallery-more" aria-hidden="true">
                +{extraImages} {extraImages === 1 ? 'image' : 'images'}
              </span>
            </>
          )}
          {showImageDiscountBadge && (
            <span
              className="product-card__discount-badge"
              aria-label={`${discountPct}% off`}
            >
              −{discountPct}%
            </span>
          )}
          {editionBadge && (
            <span className="product-card__edition-badge">{editionBadge}</span>
          )}
          {isPremium && premiumShippingMessage !== '' && (
            <span
              className="product-card__shipping"
              aria-label={`Shipping: ${premiumShippingMessage}`}
              {...(annotatePremiumFeatures
                ? { 'data-premium-feature': 'shipping' }
                : {})}
            >
              <ShippingIcon />
              <span className="product-card__shipping-text">
                {premiumShippingMessage}
              </span>
            </span>
          )}
          {showImage ? (
            <img
              className="product-card__img"
              src={image}
              alt=""
              loading="lazy"
              {...(annotatePremiumFeatures && isPremium
                ? { 'data-premium-feature': 'image' }
                : {})}
            />
          ) : (
            <span className="product-card__media-placeholder" aria-hidden="true" />
          )}
        </div>
        <div className="product-card__corner-actions">
          <CardDotsMenu
            menuId={
              productId !== ''
                ? `product-card-menu-${productId}`
                : 'product-card-menu'
            }
            alertDisabled={!hasProductLink}
            alertLabel={productAlertMenuLabel(inStock, priceUnknown)}
            onSaveAlert={openAlertSignup}
            onReportIssue={() => {
              trackReportProductOpen();
              setReportOpen(true);
            }}
            showRemoveRecentlyViewed={isRecentlyViewed}
            onRemoveRecentlyViewed={() => removeRecentlyViewedProduct(product)}
          />
        </div>
      </div>
      {layout === 'list' ? (
        <div className="product-card__body product-card__body--list">
          {bodyContent}
        </div>
      ) : (
        <div className="product-card__body">{bodyContent}</div>
      )}
      {hasProductLink && !annotatePremiumFeatures && (
        <a
          className="product-card__stretch-link"
          href={productUrlOut}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={mediaAriaLabel}
          onClick={(e) => {
            trackMysqlOutboundClick();
            trackGaOutbound('card');
            handleProductOutboundClick();
            // Let the browser follow href; avoid re-render cancelling navigation.
            e.stopPropagation();
          }}
        />
      )}
      {hasProductLink && favoriteOpen && (
        <FavoriteSignupModal
          onClose={() => setFavoriteOpen(false)}
          productUrl={productUrl}
          productTitle={typeof title === 'string' ? title : ''}
          productImage={image}
          priceNow={priceNow}
          priceWas={priceWas}
          merchant={merchant}
          inStock={inStock}
        />
      )}
      {reportOpen && (
        <ReportModal
          onClose={() => setReportOpen(false)}
          productUrl={productUrl}
          productTitle={typeof title === 'string' ? title : ''}
        />
      )}
    </article>
  );
}
